export type AudioMediaEvent = "ended" | "error" | "timeupdate";

export interface AudioMediaElement {
  src: string;
  currentTime: number;
  loop: boolean;
  muted: boolean;
  preload: "none" | "metadata" | "auto" | "";
  volume: number;
  readonly paused: boolean;
  canPlayType(type: string): CanPlayTypeResult;
  load(): void;
  pause(): void;
  play(): Promise<void>;
  addEventListener(type: AudioMediaEvent, listener: EventListener): void;
  removeEventListener(type: AudioMediaEvent, listener: EventListener): void;
}

export interface AudioPlatform {
  createAudioElement(): AudioMediaElement | undefined;
  releaseAudioElement?(element: AudioMediaElement): void;
  unlock(): Promise<void>;
}

const MEDIA_ELEMENT_POOL_SIZE = 4;
const SILENT_MP3_SOURCE =
  "data:audio/mpeg;base64,/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxDsAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxHYAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
const SILENT_WAV_SOURCE =
  "data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQIAAAAAAA==";
const SILENT_MEDIA_SOURCES = [
  { src: SILENT_MP3_SOURCE, type: "audio/mpeg" },
  { src: SILENT_WAV_SOURCE, type: "audio/wav" }
] as const;

type WebkitAudioGlobal = typeof globalThis & {
  readonly webkitAudioContext?: typeof AudioContext;
};

function getAudioContextConstructor(): typeof AudioContext | undefined {
  if (typeof AudioContext === "function") {
    return AudioContext;
  }

  const constructor = (globalThis as WebkitAudioGlobal).webkitAudioContext;
  return typeof constructor === "function" ? constructor : undefined;
}

function selectSilentMediaSource(element: AudioMediaElement): string {
  try {
    return (
      SILENT_MEDIA_SOURCES.find(
        (source) => element.canPlayType(source.type) !== ""
      )?.src ?? SILENT_WAV_SOURCE
    );
  } catch {
    return SILENT_WAV_SOURCE;
  }
}

export class BrowserAudioPlatform implements AudioPlatform {
  private context: AudioContext | undefined;
  private readonly reusableElements = new Set<AudioMediaElement>();
  private readonly availableElements: AudioMediaElement[] = [];

  createAudioElement(): AudioMediaElement | undefined {
    const reusable = this.availableElements.pop();
    if (reusable) {
      return reusable;
    }

    if (typeof Audio !== "function") {
      return undefined;
    }

    return new Audio();
  }

  releaseAudioElement(element: AudioMediaElement): void {
    if (
      !this.reusableElements.has(element) ||
      this.availableElements.includes(element)
    ) {
      return;
    }

    element.pause();
    element.loop = false;
    try {
      element.currentTime = 0;
    } catch {
      // Some browsers reject seeking while a media resource is being replaced.
    }
    this.availableElements.push(element);
  }

  async unlock(): Promise<void> {
    const mediaUnlock = this.unlockMediaElements();
    const webAudioUnlock = this.unlockWebAudio().catch(() => undefined);

    await mediaUnlock;
    await webAudioUnlock;
  }

  private async unlockMediaElements(): Promise<void> {
    if (
      typeof Audio !== "function" ||
      this.availableElements.length >= MEDIA_ELEMENT_POOL_SIZE
    ) {
      return;
    }

    const elements: AudioMediaElement[] = [];
    const playPromises: Promise<void>[] = [];
    let setupFailure: { readonly error: unknown } | undefined;

    for (
      let index = this.availableElements.length;
      index < MEDIA_ELEMENT_POOL_SIZE;
      index += 1
    ) {
      try {
        const element = new Audio();
        element.loop = false;
        element.preload = "auto";
        element.src = selectSilentMediaSource(element);
        element.load();
        elements.push(element);
        this.reusableElements.add(element);
        playPromises.push(Promise.resolve(element.play()));
      } catch (error) {
        setupFailure = { error };
        break;
      }
    }

    const playResults = await Promise.all(
      playPromises.map(async (playPromise) => {
        try {
          await playPromise;
          return { ok: true } as const;
        } catch (error) {
          return { error, ok: false } as const;
        }
      })
    );
    const playbackFailure = playResults.find((result) => !result.ok);
    const failure = setupFailure ?? playbackFailure;

    if (failure !== undefined) {
      for (const element of elements) {
        element.pause();
        this.reusableElements.delete(element);
      }
      throw failure.error;
    }

    for (const element of elements) {
      element.pause();
      try {
        element.currentTime = 0;
      } catch {
        // The silent frame is disposable; playback permission is already kept.
      }
      this.availableElements.push(element);
    }
  }

  private async unlockWebAudio(): Promise<void> {
    const AudioContextConstructor = getAudioContextConstructor();
    if (!AudioContextConstructor) {
      return;
    }

    if (!this.context || this.context.state === "closed") {
      this.context = new AudioContextConstructor();
    }

    if (this.context.state === "suspended") {
      await this.context.resume();
    }

    const buffer = this.context.createBuffer(
      1,
      1,
      this.context.sampleRate || 44_100
    );
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    source.start(0);
  }
}
